// Iter 62be — Elevate School native ASA page (calendar-first).
// Vue centrée sur le calendrier des tâches Elevate à faire pour les élèves,
// mobile-first responsive. Founder only.
import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  useElevateStatus, useElevateTasks, useElevateCashReminders, completeElevateTask, deleteElevateTask,
} from '../hooks/useElevate';
import { useIsMobile } from '../hooks/useIsMobile';
import CreateElevateTaskModal from '../components/CreateElevateTaskModal';
import {
  CheckSquare, RefreshCw, Plus, Loader2, ArrowUpRight, ChevronLeft, ChevronRight, X,
} from '../components/icons';

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const WEEKDAYS_FR = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];

export default function ElevateSchoolPage() {
  const isMobile = useIsMobile();
  const status = useElevateStatus();
  const { tasks, loading: tLoading } = useElevateTasks();
  const cashReminders = useElevateCashReminders();
  const [showCreate, setShowCreate] = useState(false);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const openTasks = tasks.filter((t) => t.status !== 'done');
  const overdueTasks = openTasks.filter((t) => {
    if (!t.due_date) return false;
    return new Date(`${t.due_date}T${t.due_time || '23:59'}:00`) < new Date();
  });
  const overdueCash = cashReminders.filter((r) => r.overdue && !r.completed);
  const uniqStudents = useMemo(() => {
    const s = new Set();
    tasks.forEach((t) => {
      const key = (t.client_email || t.client_name || '').toLowerCase().trim();
      if (key) s.add(key);
    });
    return s.size;
  }, [tasks]);

  const pad = isMobile ? '18px 16px 16px' : '32px 32px 20px';
  const bodyPad = isMobile ? '0 16px 100px' : '0 32px 80px';

  return (
    <div data-testid="elevate-school-page" style={{ minHeight: '100vh' }}>
      {/* Header responsive */}
      <header style={{ padding: pad }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap',
        }}>
          <img src="/elevate-logo.png" alt="Elevate School"
            width={isMobile ? 40 : 48} height={isMobile ? 40 : 48}
            style={{
              borderRadius: 12,
              boxShadow: '0 0 0 1px rgba(91,190,252,0.35), 0 12px 32px -12px rgba(91,190,252,0.55)',
            }}
          />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: isMobile ? 9 : 10,
              color: '#5BBEFC', letterSpacing: '0.16em', textTransform: 'uppercase',
            }}>Écosystème Elevate Influence</div>
            <h1 style={{
              margin: 0, marginTop: 2,
              fontFamily: 'Sora', fontWeight: 800,
              fontSize: isMobile ? '1.6rem' : '2.2rem',
              color: 'var(--t1)', letterSpacing: '-0.01em',
            }}>Elevate School</h1>
            <div style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: isMobile ? 11.5 : 12.5,
              color: 'var(--t3)', marginTop: 4,
            }}>
              Calendrier des tâches à faire pour les élèves
            </div>
          </div>
          <ConnectionPill connected={status.connected} />
        </div>

        {/* Toolbar buttons wrap on mobile */}
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap',
          marginTop: 14,
        }}>
          <button
            data-testid="elevate-school-refresh"
            onClick={() => { window.dispatchEvent(new CustomEvent('asa-elevate-refresh')); toast.success('Sync en cours…'); }}
            className="asa-btn"
            style={{ flex: isMobile ? '1 1 auto' : 'unset', justifyContent: 'center' }}
            title="Synchroniser depuis Elevate"
          ><RefreshCw size={13} strokeWidth={2.2} /> Sync</button>
          <button
            data-testid="elevate-school-new-task"
            onClick={() => setShowCreate(true)}
            className="asa-btn"
            style={{
              flex: isMobile ? '1 1 auto' : 'unset', justifyContent: 'center',
              background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
              border: 'none', color: '#0B1330',
            }}
          ><Plus size={14} strokeWidth={2.4} /> Nouvelle tâche</button>
        </div>

        {/* CTA vers l'app déployée */}
        {status.public_url && (
          <a
            href={status.public_url}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="elevate-school-open-external"
            style={{
              display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14,
              marginTop: 16, padding: isMobile ? '12px 14px' : '14px 18px',
              borderRadius: 14, textDecoration: 'none',
              background:
                'radial-gradient(120% 100% at 100% 0%, rgba(91,190,252,0.24) 0%, rgba(91,190,252,0) 55%),'
                + ' linear-gradient(160deg, rgba(30,40,84,0.85) 0%, rgba(14,20,50,0.98) 100%)',
              border: '1.5px solid rgba(91,190,252,0.45)',
              boxShadow: '0 18px 42px -22px rgba(91,190,252,0.55), inset 0 1px 0 rgba(255,255,255,0.06)',
            }}
          >
            <img src="/elevate-logo.png" alt="" width={isMobile ? 30 : 36} height={isMobile ? 30 : 36}
              style={{ borderRadius: 10, boxShadow: '0 0 0 1px rgba(91,190,252,0.35)', flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5,
                color: '#5BBEFC', letterSpacing: '0.14em', textTransform: 'uppercase',
              }}>Aperçu ci-dessous · vue complète →</div>
              <div style={{
                fontFamily: 'Sora', fontWeight: 800, fontSize: isMobile ? 13 : 15,
                color: 'var(--t1)', marginTop: 3,
              }}>Ouvrir Elevate School</div>
              {!isMobile && (
                <div style={{
                  fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
                  color: 'var(--t4)', marginTop: 3,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{status.public_url.replace(/^https?:\/\//, '')}</div>
              )}
            </div>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
              padding: isMobile ? '8px 12px' : '10px 14px', borderRadius: 10,
              background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
              color: '#0B1330',
              fontFamily: 'Sora', fontWeight: 800, fontSize: isMobile ? 11 : 12,
            }}>
              Ouvrir <ArrowUpRight size={12} strokeWidth={2.4} />
            </span>
          </a>
        )}
      </header>

      <div style={{ padding: bodyPad }}>
        {/* KPIs */}
        <section style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? 8 : 12,
          marginBottom: isMobile ? 16 : 20,
        }}>
          <KpiCard label="Élèves suivis" value={uniqStudents} accent="#5BBEFC" mobile={isMobile} />
          <KpiCard label="Tâches en cours" value={openTasks.length} accent="#A78BFA" mobile={isMobile} />
          <KpiCard label="Tâches en retard" value={overdueTasks.length} accent="#FF6B6B" priority={overdueTasks.length > 0} mobile={isMobile} />
          <KpiCard label="Relances cash" value={overdueCash.length} accent="#F5C56A" priority={overdueCash.length > 0} mobile={isMobile} />
        </section>

        {/* Calendar */}
        <section>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
            flexWrap: 'wrap',
          }}>
            <button
              data-testid="elevate-cal-prev"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              className="asa-btn"
              style={{ padding: '6px 10px' }}
              aria-label="Mois précédent"
            ><ChevronLeft size={14} /></button>
            <button
              data-testid="elevate-cal-next"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              className="asa-btn"
              style={{ padding: '6px 10px' }}
              aria-label="Mois suivant"
            ><ChevronRight size={14} /></button>
            <button
              data-testid="elevate-cal-today"
              onClick={() => { const d = new Date(); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); }}
              className="asa-btn"
              style={{ padding: '6px 12px' }}
            >Aujourd&apos;hui</button>
            <h2 style={{
              margin: 0, marginLeft: 6, flex: 1,
              fontFamily: 'Sora', fontWeight: 800, fontSize: isMobile ? 16 : 18,
              color: 'var(--t1)',
            }}>
              {MONTHS_FR[cursor.getMonth()]} {cursor.getFullYear()}
            </h2>
            <span style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
              color: 'var(--t3)',
            }}>{tasks.length} tâche{tasks.length > 1 ? 's' : ''}</span>
          </div>

          {tLoading ? (
            <div className="shimmer" style={{ height: 360, borderRadius: 14 }} />
          ) : (
            <MonthCalendar cursor={cursor} tasks={tasks} isMobile={isMobile} />
          )}
        </section>
      </div>

      {showCreate && (
        <CreateElevateTaskModal
          onClose={() => setShowCreate(false)}
          onCreated={() => window.dispatchEvent(new CustomEvent('asa-elevate-refresh'))}
        />
      )}
    </div>
  );
}

/* ---------- Month calendar ---------- */

function MonthCalendar({ cursor, tasks, isMobile }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  // Monday=0 … Sunday=6
  const startOffset = (firstDay.getDay() + 6) % 7;
  const totalCells = Math.ceil((startOffset + lastDay.getDate()) / 7) * 7;

  const tasksByDay = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      if (!t.due_date) return;
      (map[t.due_date] ||= []).push(t);
    });
    return map;
  }, [tasks]);

  const cells = [];
  for (let i = 0; i < totalCells; i += 1) {
    const dayNum = i - startOffset + 1;
    const inMonth = dayNum >= 1 && dayNum <= lastDay.getDate();
    const iso = inMonth
      ? `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`
      : null;
    cells.push({ i, dayNum, inMonth, iso });
  }

  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return (
    <div
      data-testid="elevate-calendar"
      style={{
        borderRadius: 14, overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.06)',
        background: 'var(--sf)',
      }}
    >
      {/* Weekday header */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        {WEEKDAYS_FR.map((w) => (
          <div key={w} style={{
            padding: '10px 6px', textAlign: 'center',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5,
            color: 'var(--t3)', letterSpacing: '0.08em', textTransform: 'uppercase',
            background: 'rgba(255,255,255,0.02)',
          }}>{w}</div>
        ))}
      </div>

      {/* Cells */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        gridAutoRows: isMobile ? 78 : 108,
      }}>
        {cells.map(({ i, dayNum, inMonth, iso }) => {
          const dayTasks = iso ? (tasksByDay[iso] || []) : [];
          const isToday = iso === todayIso;
          const isPast = iso && iso < todayIso;
          return (
            <div
              key={i}
              data-testid={iso ? `elevate-cal-cell-${iso}` : undefined}
              style={{
                position: 'relative',
                padding: 6, overflow: 'hidden',
                borderRight: (i + 1) % 7 !== 0 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                borderTop: '1px solid rgba(255,255,255,0.05)',
                background: isToday ? 'rgba(91,190,252,0.08)' : inMonth ? 'transparent' : 'rgba(0,0,0,0.18)',
                opacity: inMonth ? 1 : 0.35,
              }}
            >
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 4,
              }}>
                <span style={{
                  fontFamily: 'Sora', fontWeight: isToday ? 800 : 600, fontSize: 11,
                  color: isToday ? '#5BBEFC' : isPast ? 'var(--t4)' : 'var(--t2)',
                }}>{inMonth ? dayNum : ''}</span>
                {dayTasks.length > 0 && (
                  <span style={{
                    padding: '1px 5px', borderRadius: 999,
                    background: 'rgba(91,190,252,0.20)',
                    color: '#5BBEFC',
                    fontFamily: 'Sora', fontWeight: 800, fontSize: 9,
                  }}>{dayTasks.length}</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {dayTasks.slice(0, isMobile ? 1 : 3).map((t) => (
                  <CalendarTaskPill key={t.id} task={t} isPast={isPast} />
                ))}
                {dayTasks.length > (isMobile ? 1 : 3) && (
                  <span style={{
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 9,
                    color: 'var(--t4)', paddingLeft: 4,
                  }}>+{dayTasks.length - (isMobile ? 1 : 3)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CalendarTaskPill({ task, isPast }) {
  const [busy, setBusy] = useState(false);
  const [localStatus, setLocalStatus] = useState(task.status);
  const [hidden, setHidden] = useState(false);
  const isDone = localStatus === 'done';
  const isOverdue = isPast && !isDone;
  const label = task.client_name ? `${task.client_name} · ${task.title}` : task.title;
  const color = isOverdue ? '#FF6B6B' : isDone ? '#34D399' : '#5BBEFC';

  const toggle = async (e) => {
    e?.stopPropagation?.();
    setBusy(true);
    const next = isDone ? 'todo' : 'done';
    setLocalStatus(next);
    try {
      const r = await completeElevateTask(task.id, next);
      toast.success(r.remote_synced ? 'Sync Elevate ✓' : 'Coché localement');
      if (navigator.vibrate) navigator.vibrate(15);
    } catch (_) {
      setLocalStatus(task.status);
      toast.error('Erreur');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (e) => {
    e?.stopPropagation?.();
    if (!window.confirm(`Supprimer « ${task.title} » ?`)) return;
    setBusy(true);
    try {
      await deleteElevateTask(task.id);
      toast.success('Tâche supprimée');
      if (navigator.vibrate) navigator.vibrate(15);
      setHidden(true);
      window.dispatchEvent(new CustomEvent('asa-elevate-refresh'));
    } catch (_) {
      toast.error('Erreur suppression');
    } finally {
      setBusy(false);
    }
  };

  if (hidden) return null;

  return (
    <div
      data-testid={`elevate-cal-task-${task.id}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '3px 4px 3px 6px', borderRadius: 5,
        background: `${color}18`,
        border: `1px solid ${color}44`,
        color: 'var(--t1)',
        fontFamily: 'Sora', fontWeight: 700, fontSize: 10,
        overflow: 'hidden',
      }}
    >
      <button
        onClick={toggle}
        disabled={busy}
        title={label}
        style={{
          display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0,
          background: 'transparent', border: 'none', cursor: busy ? 'wait' : 'pointer',
          textAlign: 'left', padding: 0, color: 'inherit', font: 'inherit',
        }}
      >
        {busy ? (
          <Loader2 size={9} className="spin" />
        ) : isDone ? (
          <CheckSquare size={9} color={color} strokeWidth={2.4} />
        ) : (
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: color, flexShrink: 0,
            boxShadow: `0 0 6px ${color}`,
          }} />
        )}
        <span style={{
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          textDecoration: isDone ? 'line-through' : 'none',
          opacity: isDone ? 0.55 : 1,
        }}>{label}</span>
      </button>
      <button
        onClick={remove}
        title="Supprimer"
        aria-label="Supprimer"
        style={{
          background: 'transparent', border: 'none',
          padding: 0, cursor: 'pointer', color: '#F0616B',
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}
      >
        <X size={9} strokeWidth={2.4} />
      </button>
    </div>
  );
}

/* ---------- Small subcomponents ---------- */

function ConnectionPill({ connected }) {
  const color = connected ? '#34D399' : '#FF6B6B';
  return (
    <span
      data-testid="elevate-school-connection"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '5px 10px', borderRadius: 999,
        background: `${color}18`, border: `1px solid ${color}55`, color,
        fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        alignSelf: 'flex-start',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}` }} />
      {connected ? 'Connecté' : 'Déconnecté'}
    </span>
  );
}

function KpiCard({ label, value, accent, priority, mobile }) {
  return (
    <div
      className="asa-card"
      data-testid={`elevate-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        padding: mobile ? 12 : 14, position: 'relative', overflow: 'hidden',
        background:
          `radial-gradient(120% 100% at 100% 0%, ${accent}20 0%, ${accent}00 55%),`
          + ' var(--sf)',
        border: `1.5px solid ${priority ? accent + '99' : accent + '35'}`,
        boxShadow: priority ? `0 10px 24px -12px ${accent}55, inset 0 1px 0 rgba(255,255,255,0.06)` : 'inset 0 1px 0 rgba(255,255,255,0.04)',
      }}
    >
      <div style={{
        fontFamily: 'Sora', fontWeight: 800, fontSize: mobile ? 8.5 : 9.5,
        color: accent, letterSpacing: '0.12em', textTransform: 'uppercase',
      }}>{label}</div>
      <div style={{
        fontFamily: 'Sora', fontWeight: 800, fontSize: mobile ? 22 : 28,
        color: 'var(--t1)', marginTop: 6, letterSpacing: '-0.02em',
      }}>{value}</div>
    </div>
  );
}
